<script setup>
import {computed, defineProps, nextTick, onMounted, onUnmounted, ref, watch} from 'vue';
import {router, usePage} from '@inertiajs/vue3';
import {getActiveLanguage} from 'laravel-vue-i18n'
import EnglishFlagIcon from "@/Svgs/Flags/English.svg.vue";
import FrenchFlagIcon from "@/Svgs/Flags/French.svg.vue";

const page = usePage();
const user = computed(() => page.props.auth.user);
const isRequiredAuthCode = computed(() => page.props.auth.requiredAuthCode);

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    },
    clickPosition: {
        type: Object,
        default: {
            left: 0,
            width: 0
        }
    }
});

const emit = defineEmits(['close']);

const arrowLeft = ref(0);

const updateArrowPosition = () => {
    const dropdown = document.querySelector('.languagesSwitcherComponent');
    if (dropdown) {
        const dropdownRect = dropdown.getBoundingClientRect();
        arrowLeft.value = props.clickPosition.left - dropdownRect.left + props.clickPosition.width / 2 - 11;
    }
};

watch(() => props.clickPosition, (newVal) => {
    if (props.isOpen) {
        nextTick(updateArrowPosition);
    }
});

const closeDropdown = () => {
    emit('close');
}

const handleClickOutside = (event) => {
    const dropdown = document.querySelector('.languagesSwitcherComponent');
    if (dropdown && ! dropdown.contains(event.target) && props.isOpen) {
        closeDropdown();
    }
}

const handleRouteChange = () => {
    if (props.isOpen) {
        closeDropdown();
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
    router.on('success', handleRouteChange);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
    <Transition :duration="550" name="languagesSwitcherComponent-transition">
        <div :class="['languagesSwitcherComponent', { '-is-visible': isOpen}]" v-show="isOpen">
            <div class="languagesSwitcherComponent-body">
                <a
                    v-if="getActiveLanguage() === 'en'"
                    :href="route(user && ! isRequiredAuthCode ? 'dashboard' : 'login', {locale: 'fr'})"
                    class="-is-clickable"
                >
                    <FrenchFlagIcon />
                    <span>Français</span>
                </a>
                <a
                    v-else
                    :href="route(user && ! isRequiredAuthCode ? 'dashboard' : 'login', {locale: 'en'})"
                    class="-is-clickable"
                >
                    <EnglishFlagIcon />
                    <span>English</span>
                </a>
            </div>
        </div>
    </Transition>
</template>

<style lang="scss" scoped>
@use 'include-media/dist/include-media' as *;
@use '~@/base/variables' as *;

.languagesSwitcherComponent {
    position: absolute;
    top: 46px;
    right: 20px;
    width: 130px;
    background-color: $gray300;
    z-index: 10;
    transform: translateY(0);
    opacity: 1;
    padding: 0 15px 15px;
    border-bottom-right-radius: 20px;
    border-bottom-left-radius: 20px;

    @include media($breakpoint-phone) {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        border-radius: 0;
        height: calc(100vh - 54px);
        display: flex;
        justify-content: center;
    }
    &-transition {
        &-enter {
            &-active {
                transition: all 0.3s ease-out;
            }
        }
        &-leave {
            &-active {
                transition: all 0.3s ease-out;
            }
        }
        &-enter-from,
        &-leave-to {
            transform: translateY(-10px);
            opacity: 0;
        }
    }
    &-body {
        display: flex;
        justify-content: flex-end;

        a {
            display: flex;
            align-items: center;
            gap: 10px;
            text-transform: uppercase;
            font-size: 14px;
            color: $blue800;
            text-decoration: none;
        }
    }
}
</style>
